import type { MouseEvent as ReactMouseEvent } from 'react'
import { useRef, useState } from 'react'
import { fetchTillConfig } from '@/components/KioskShell'
import LanguageToggle from '@/components/ui/LanguageToggle'
import { forceUpdate } from '@/hooks/useAutoUpdate'
import { gql } from '@/lib/api'
import { CREATE_ACCESS_TOKEN } from '@/lib/queries'
import { KIOSK_BUILD_DATE, KIOSK_VERSION } from '@/lib/version'
import { useKioskStore } from '@/stores/kiosk-store'

const TOKEN_KEY = 'kiosk_token'
const LONG_PRESS_MS = 3000

export default function KioskHeader() {
  const config = useKioskStore((s) => s.config)
  const menuType = useKioskStore((s) => s.menuType)
  const restaurantName = useKioskStore((s) => s.restaurantName)
  const tillName = useKioskStore((s) => s.tillName)
  const parseToken = useKioskStore((s) => s.parseToken)
  const setParseToken = useKioskStore((s) => s.setParseToken)
  const locale = useKioskStore((s) => s.locale)
  const remoteConfig = useKioskStore((s) => s.remoteConfig)
  const currentCategoryLabel = useKioskStore((s) => s.currentCategoryLabel)
  const backAction = useKioskStore((s) => s.backAction)
  const posTerminalId = useKioskStore((s) => s.posTerminalId)
  const posTerminalProvider = useKioskStore((s) => s.posTerminalProvider)
  const posTerminals = useKioskStore((s) => s.posTerminals)
  const receiptPrinterId = useKioskStore((s) => s.receiptPrinterId)
  const paymentMethods = useKioskStore((s) => s.paymentMethods)
  const preferredApiEnvironment = useKioskStore((s) => s.preferredApiEnvironment)
  const lastPaymentDebug = useKioskStore((s) => s.lastPaymentDebug)
  const sv = locale === 'sv'
  const isLandscape = remoteConfig?.layoutMode === 'landscape'

  const [showLogin, setShowLogin] = useState(false)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loginLoading, setLoginLoading] = useState(false)
  const [loginError, setLoginError] = useState<string | null>(null)
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleLogoTouchStart = () => {
    longPressTimer.current = setTimeout(() => {
      setShowLogin(true)
    }, LONG_PRESS_MS)
  }

  const handleLogoTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }

  const handleLogin = async () => {
    if (!config || !username || !password) return
    setLoginLoading(true)
    setLoginError(null)
    try {
      const data = await gql(config.apiEndpoint, CREATE_ACCESS_TOKEN, { username, password })
      const token = data.createAccessToken.parseToken
      localStorage.setItem(TOKEN_KEY, token)
      setParseToken(token)
      // Fetch till config with new token
      await fetchTillConfig(config.apiEndpoint, token, config.restaurantId, config.tillId)
      setShowLogin(false)
      setUsername('')
      setPassword('')
    } catch (e) {
      setLoginError(e instanceof Error ? e.message : 'Inloggningen misslyckades')
    } finally {
      setLoginLoading(false)
    }
  }

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY)
    setParseToken(null)
    setShowLogin(false)
  }

  const handleForceUpdate = () => forceUpdate()

  const logoLongPressProps = {
    onTouchStart: handleLogoTouchStart,
    onTouchEnd: handleLogoTouchEnd,
    onTouchCancel: handleLogoTouchEnd,
    onMouseDown: handleLogoTouchStart,
    onMouseUp: handleLogoTouchEnd,
    onMouseLeave: handleLogoTouchEnd,
    onContextMenu: (e: ReactMouseEvent) => e.preventDefault(),
  }

  return (
    <>
      {/* ─── Consolidated header (same design in portrait and landscape) ─── */}
      {/* Portrait keeps the same structure as landscape but with slightly smaller title font */}
      <div className={`shrink-0 bg-kiosk-surface border-b border-kiosk-surface-2 relative ${isLandscape ? 'px-8 py-6' : 'px-6 py-5'}`}>
        {/* LEFT: Logo + Back arrow */}
        <div className={`absolute top-1/2 -translate-y-1/2 flex items-center gap-3 ${isLandscape ? 'left-8' : 'left-6'}`}>
          {remoteConfig?.logoUrl && (
            <div className="w-20 h-20 shrink-0 flex items-center justify-center cursor-pointer" {...logoLongPressProps}>
              <img src={remoteConfig.logoUrl} alt="" className="w-16 h-16 object-contain rounded-full pointer-events-none" />
            </div>
          )}
          {backAction && (
            <button
              type="button"
              onClick={() => {
                if (backAction) backAction()
              }}
              className="kiosk-btn h-14 pl-4 pr-5 rounded-full flex items-center gap-2.5 bg-kiosk-bg border border-kiosk-surface-2 active:bg-kiosk-surface-2 text-kiosk-text"
            >
              <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
                <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
              </svg>
              <span className="font-semibold text-base">{sv ? 'Tillbaka' : 'Back'}</span>
            </button>
          )}
        </div>

        {/* CENTER: Page title — absolutely centered on screen width */}
        {currentCategoryLabel && (
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
            <span className={`text-kiosk-text font-bold uppercase tracking-widest whitespace-nowrap ${isLandscape ? 'text-3xl' : 'text-2xl'}`}>{currentCategoryLabel}</span>
          </div>
        )}

        {/* RIGHT: Menu type badge + language flags only */}
        <div className={`absolute top-1/2 -translate-y-1/2 flex items-center gap-4 ${isLandscape ? 'right-8' : 'right-6'}`}>
          {menuType && (
            <span className={`text-kiosk-accent font-semibold rounded-full bg-kiosk-accent/10 border border-kiosk-accent/30 whitespace-nowrap px-4 py-2 ${isLandscape ? 'text-base' : 'text-sm'}`}>
              {menuType === 'EatIn' ? (sv ? '🍽 Äta här' : '🍽 Eat in') : sv ? '🛍 Ta med' : '🛍 Takeaway'}
            </span>
          )}
          <LanguageToggle position="top" flagsOnly />
        </div>

        {/* Invisible spacer to give the bar its height */}
        <div className="h-20" />
      </div>

      {/* Hidden login modal — triggered by 5s long-press on logo */}
      {showLogin && (
        <div className="fixed inset-0 z-200 flex items-center justify-center kiosk-fade-in">
          <div className="absolute inset-0 bg-black/80" onClick={() => setShowLogin(false)} />
          <div className="relative bg-kiosk-surface w-[85%] max-w-sm rounded-3xl p-8 z-10 space-y-5">
            <h2 className="text-xl font-bold text-kiosk-text text-center">Admin</h2>

            {parseToken ? (
              <div className="space-y-4 text-center">
                <div className="flex items-center justify-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-green-400 text-sm">Inloggad</span>
                </div>
                <button onClick={handleLogout} className="kiosk-btn w-full py-4 bg-red-500/20 text-red-400 font-semibold rounded-xl border border-red-500/30">
                  Logga ut
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Användarnamn"
                  autoComplete="username"
                  className="w-full px-4 py-4 bg-kiosk-bg border border-kiosk-surface-2 rounded-xl text-kiosk-text placeholder:text-kiosk-muted text-base focus:outline-none focus:border-kiosk-accent"
                />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Lösenord"
                  autoComplete="current-password"
                  onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                  className="w-full px-4 py-4 bg-kiosk-bg border border-kiosk-surface-2 rounded-xl text-kiosk-text placeholder:text-kiosk-muted text-base focus:outline-none focus:border-kiosk-accent"
                />
                {loginError && <p className="text-red-400 text-sm text-center">{loginError}</p>}
                <button
                  onClick={handleLogin}
                  disabled={loginLoading || !username || !password}
                  className="kiosk-btn w-full py-4 bg-kiosk-accent text-kiosk-bg font-bold rounded-xl text-base disabled:opacity-50"
                >
                  {loginLoading ? 'Loggar in...' : 'Logga in'}
                </button>
              </div>
            )}

            <button onClick={() => setShowLogin(false)} className="kiosk-btn w-full py-2 text-kiosk-muted text-sm">
              Stäng
            </button>

            {/* Version info + force-update at bottom of admin modal */}
            <div className="pt-3 border-t border-kiosk-surface-2 -mx-2 space-y-2">
              <div className="text-center">
                <p className="text-kiosk-accent text-base font-bold">{restaurantName || remoteConfig?.appName || 'Okänd restaurang'}</p>
                <p className="text-kiosk-text/80 text-sm font-medium">{tillName || (config?.tillId ? `Kassa ${config.tillId.slice(0, 6)}` : 'Okänd kassa')}</p>
                <p className="text-kiosk-muted/60 text-xs mt-1">
                  Kiosk {KIOSK_VERSION} · {KIOSK_BUILD_DATE}
                </p>
                <p className="text-kiosk-muted/60 text-xs mt-1">
                  Layout: <span className={remoteConfig?.layoutMode === 'landscape' ? 'text-kiosk-accent font-semibold' : ''}>{remoteConfig?.layoutMode ?? 'okänd (ej inloggad?)'}</span>
                  {remoteConfig ? ' · till-config läst ✓' : ' · till-config saknas ✗'}
                </p>
                {/* Betal-diagnostik — visar exakt vad kiosken har läst från LocoPOS för just den här kassan */}
                <div className="mt-2 pt-2 border-t border-kiosk-surface-2/50 text-left px-2">
                  <p className="text-kiosk-muted/60 text-[11px] font-semibold uppercase tracking-wider mb-1">Betal-diagnostik</p>
                  <p className="text-kiosk-muted/70 text-[11px]">
                    Vald terminal: <span className={posTerminalProvider ? 'text-kiosk-accent' : 'text-red-400'}>{posTerminalProvider || 'INGEN'}</span>
                    {posTerminalId && <span className="text-kiosk-muted/40"> · {posTerminalId.slice(0, 8)}…</span>}
                  </p>
                  {/* Alla terminaler som finns på kassan (inkl offline) — hjälper identifiera LocoPOS-konfig-problem */}
                  {posTerminals.length > 0 && (
                    <div className="text-kiosk-muted/60 text-[10px] ml-2">
                      {posTerminals.map((t) => (
                        <p key={t.id}>
                          • {t.name}: {t.paymentProvider} · {t.isOnline ? <span className="text-green-400">ONLINE</span> : <span className="text-red-400">OFFLINE</span>} ·{' '}
                          {t.isActive ? 'aktiv' : 'inaktiv'}
                        </p>
                      ))}
                    </div>
                  )}
                  {posTerminals.length === 0 && <p className="text-red-400 text-[10px] ml-2">Inga terminaler kopplade till kassan</p>}
                  <p className="text-kiosk-muted/70 text-[11px] mt-1">
                    Metoder: <span className="text-kiosk-text/70">{paymentMethods.length > 0 ? paymentMethods.map((m) => m.paymentProvider).join(', ') : 'INGA'}</span>
                  </p>
                  <p className="text-kiosk-muted/70 text-[11px]">
                    Skrivarkö: <span className={receiptPrinterId ? 'text-kiosk-accent' : 'text-red-400'}>{receiptPrinterId ? 'OK' : 'SAKNAS'}</span>
                  </p>
                  <p className="text-kiosk-muted/70 text-[11px]">
                    API-miljö:{' '}
                    <span className={preferredApiEnvironment === 'Production' ? 'text-green-400' : preferredApiEnvironment ? 'text-yellow-400' : 'text-red-400'}>
                      {preferredApiEnvironment || 'ej satt'}
                    </span>
                  </p>
                  <p className="text-kiosk-muted/70 text-[11px]">
                    Till-ID: <span className="text-kiosk-text/60">{config?.tillId?.slice(0, 10)}…</span>
                  </p>
                  {lastPaymentDebug && (
                    <p className="text-kiosk-muted/70 text-[10px] mt-1">
                      Senaste betalning: <span className="text-kiosk-accent/80">{lastPaymentDebug}</span>
                    </p>
                  )}
                </div>
              </div>
              <button onClick={handleForceUpdate} className="kiosk-btn w-full py-2 text-kiosk-accent text-xs border border-kiosk-accent/30 rounded-lg">
                ↻ Hämta senaste version
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
